import { useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import type { SurveyAnswers, SurveyDefinition, SurveyQuestion } from '../../api/intake';

/**
 * Renders a conference-intake survey (CONFERENCE-INTAKE) for a participant
 * on a tablet: big touch targets, one control per question type, `show_if`
 * follow-ups that appear only when they apply. Controlled — the caller owns
 * `answers` — and `validateSurvey` mirrors the server's rules
 * (api/app/services/intake.py `validate_survey_answers`) so problems show
 * inline before submitting instead of as one long 422 string.
 *
 * Numbers are held as typed (strings) while editing; `cleanSurveyAnswers`
 * turns them into numbers for the submission.
 */

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MONTH_YEAR = /^(0?[1-9]|1[0-2])\/(19|20)\d{2}$/;
const OTHER = '__other__';

/**
 * Whether an answer satisfies a `show_if` (INTAKE-SHOWIF-TYPES, review
 * INTAKE-1/2). The same rule as the server's `_matches` in services/intake.py
 * — they must agree, or an answer the form shows is dropped on submit.
 *
 * It used to be `===` on the raw value: a number question is a string while
 * typed but submitted as a number, so the server's `5 == "5"` hid the
 * follow-up and discarded its answer with no error; a multi-choice answer is
 * an array and never equalled anything. Now: trimmed text, numbers by value,
 * and a multi-choice answer matches when any of its choices does.
 */
export function matchesShowIf(answer: unknown, equals: string): boolean {
  const want = String(equals).trim();
  const same = (v: unknown): boolean => {
    if (v === null || v === undefined || typeof v === 'object') return false;
    const got = String(v).trim();
    if (got === want) return true;
    if (got === '' || want === '') return false;
    const a = Number(got);
    const b = Number(want);
    return Number.isFinite(a) && Number.isFinite(b) && a === b;
  };
  return Array.isArray(answer) ? answer.some(same) : same(answer);
}

export function isQuestionVisible(q: SurveyQuestion, answers: SurveyAnswers): boolean {
  return !q.show_if || matchesShowIf(answers[q.show_if.id], q.show_if.equals);
}

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v as object).length === 0;
  return false;
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Drop answers to questions that are hidden now (a follow-up whose
 *  condition stopped holding). Repeats so a chain of follow-ups clears. */
export function pruneHidden(def: SurveyDefinition, answers: SurveyAnswers): SurveyAnswers {
  let out = answers;
  for (let pass = 0; pass < def.questions.length; pass++) {
    let changed = false;
    for (const q of def.questions) {
      if (q.id in out && !isQuestionVisible(q, out)) {
        if (!changed) out = { ...out };
        delete out[q.id];
        changed = true;
      }
    }
    if (!changed) break;
  }
  return out;
}

/** Total of a percent split's entered parts (null when nothing entered). */
export function splitTotal(q: SurveyQuestion, value: unknown): number | null {
  if (!value || typeof value !== 'object') return null;
  let total = 0;
  let any = false;
  for (const part of q.parts ?? []) {
    const n = toNum((value as Record<string, unknown>)[part]);
    if (n !== null) {
      total += n;
      any = true;
    }
  }
  return any ? total : null;
}

/** Client-side mirror of the server's survey rules → { questionId: message }
 *  for every visible question with a problem. Empty object = OK to submit. */
export function validateSurvey(
  def: SurveyDefinition,
  answers: SurveyAnswers,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const q of def.questions) {
    if (q.type === 'heading' || !isQuestionVisible(q, answers)) continue;
    const raw = answers[q.id];
    if (isEmpty(raw)) {
      if (q.required) errors[q.id] = 'Please answer this question';
      continue;
    }
    switch (q.type) {
      case 'text':
      case 'long_text':
        if (typeof raw !== 'string') errors[q.id] = 'Expected text';
        break;
      case 'email':
        if (typeof raw !== 'string' || !EMAIL.test(raw.trim())) {
          errors[q.id] = 'Enter a valid email address, e.g. name@hospital.org';
        }
        break;
      case 'number':
      case 'percent': {
        const n = toNum(raw);
        const lo = q.type === 'percent' ? 0 : q.min;
        const hi = q.type === 'percent' ? 100 : q.max;
        if (n === null) errors[q.id] = 'Enter a number';
        else if (lo !== undefined && hi !== undefined && (n < lo || n > hi)) {
          errors[q.id] = `Must be between ${lo} and ${hi}`;
        } else if (lo !== undefined && n < lo) errors[q.id] = `Must be at least ${lo}`;
        else if (hi !== undefined && n > hi) errors[q.id] = `Must be at most ${hi}`;
        break;
      }
      case 'single':
        if (typeof raw !== 'string') errors[q.id] = 'Choose one';
        else if (!(q.options ?? []).includes(raw) && !q.allow_other) {
          errors[q.id] = 'Choose one of the options';
        }
        break;
      case 'multi':
        if (!Array.isArray(raw)) errors[q.id] = 'Choose one or more';
        else if (!q.allow_other && raw.some((x) => !(q.options ?? []).includes(x))) {
          errors[q.id] = 'Choose from the options';
        }
        break;
      case 'yesno':
        if (raw !== 'Yes' && raw !== 'No') errors[q.id] = 'Answer Yes or No';
        break;
      case 'date':
        if (typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(raw) || Number.isNaN(Date.parse(raw))) {
          errors[q.id] = 'Enter a date';
        }
        break;
      case 'month_year':
        if (typeof raw !== 'string' || !MONTH_YEAR.test(raw.trim())) {
          errors[q.id] = 'Enter the month and year, e.g. 03 / 2024';
        }
        break;
      case 'percent_split': {
        if (typeof raw !== 'object' || Array.isArray(raw)) {
          errors[q.id] = 'Enter a percentage per part';
          break;
        }
        const bad = (q.parts ?? []).find((part) => {
          const n = toNum((raw as Record<string, unknown>)[part]);
          return n !== null && (n < 0 || n > 100);
        });
        if (bad) {
          errors[q.id] = `${bad}: must be between 0 and 100`;
          break;
        }
        const total = splitTotal(q, raw);
        if (total !== null && Math.abs(total - 100) > 1) {
          errors[q.id] = `Should add up to 100% (now ${fmt(total)}%)`;
        }
        break;
      }
    }
  }
  return errors;
}

/** The answers as the server wants them: hidden/empty ones dropped, numbers
 *  as numbers, text trimmed. */
export function cleanSurveyAnswers(def: SurveyDefinition, answers: SurveyAnswers): SurveyAnswers {
  const out: SurveyAnswers = {};
  for (const q of def.questions) {
    if (q.type === 'heading' || !isQuestionVisible(q, answers)) continue;
    const raw = answers[q.id];
    if (isEmpty(raw)) continue;
    if (q.type === 'number' || q.type === 'percent') {
      const n = toNum(raw);
      if (n !== null) out[q.id] = n;
    } else if (q.type === 'percent_split' && raw && typeof raw === 'object') {
      const split: Record<string, number> = {};
      for (const part of q.parts ?? []) {
        const n = toNum((raw as Record<string, unknown>)[part]);
        if (n !== null) split[part] = n;
      }
      if (Object.keys(split).length) out[q.id] = split;
    } else if (q.type === 'multi' && Array.isArray(raw)) {
      const list = (raw as string[]).map((x) => x.trim()).filter(Boolean);
      if (list.length) out[q.id] = list;
    } else if (typeof raw === 'string') {
      const t = raw.trim();
      if (t) out[q.id] = t;
    } else {
      out[q.id] = raw;
    }
  }
  return out;
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

const bigChoice = { '& .MuiFormControlLabel-label': { fontSize: '1.05rem' }, minHeight: 44 };

export function SurveyForm({
  definition,
  answers,
  onChange,
  errors = {},
  showHeader = true,
  disabled = false,
}: {
  definition: SurveyDefinition;
  answers: SurveyAnswers;
  onChange: (answers: SurveyAnswers) => void;
  /** Inline messages by question id (usually validateSurvey's output). */
  errors?: Record<string, string>;
  showHeader?: boolean;
  disabled?: boolean;
}) {
  // "Other" ticked before anything is typed — not representable in the
  // answer itself, so it lives here.
  const [otherOn, setOtherOn] = useState<Record<string, boolean>>({});

  const set = (id: string, value: unknown) => {
    const next = { ...answers };
    if (isEmpty(value)) delete next[id];
    else next[id] = value;
    onChange(pruneHidden(definition, next));
  };

  const renderQuestion = (q: SurveyQuestion) => {
    const err = errors[q.id];
    const value = answers[q.id];
    const labelSx = { fontSize: '1.05rem', fontWeight: 500, color: 'text.primary', mb: 1 };

    switch (q.type) {
      case 'heading':
        return (
          <Box key={q.id} sx={{ pt: 2 }}>
            <Typography variant="h6" component="h3">
              {q.label}
            </Typography>
            {q.help && (
              <Typography variant="body2" color="text.secondary">
                {q.help}
              </Typography>
            )}
            <Divider sx={{ mt: 1 }} />
          </Box>
        );

      case 'text':
      case 'long_text':
      case 'email':
        return (
          <TextField
            key={q.id}
            label={q.label}
            required={q.required}
            type={q.type === 'email' ? 'email' : 'text'}
            multiline={q.type === 'long_text'}
            minRows={q.type === 'long_text' ? 3 : undefined}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => set(q.id, e.target.value)}
            error={Boolean(err)}
            helperText={err ?? q.help}
            disabled={disabled}
            fullWidth
          />
        );

      case 'number':
      case 'percent':
        return (
          <TextField
            key={q.id}
            label={q.label}
            required={q.required}
            type="number"
            value={value === undefined || value === null ? '' : String(value)}
            onChange={(e) => set(q.id, e.target.value)}
            error={Boolean(err)}
            helperText={err ?? q.help}
            disabled={disabled}
            sx={{ maxWidth: 420 }}
            fullWidth
            slotProps={{
              htmlInput: {
                inputMode: 'decimal',
                min: q.type === 'percent' ? 0 : q.min,
                max: q.type === 'percent' ? 100 : q.max,
              },
              input:
                q.type === 'percent'
                  ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                  : undefined,
            }}
          />
        );

      case 'date':
        return (
          <TextField
            key={q.id}
            label={q.label}
            required={q.required}
            type="date"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => set(q.id, e.target.value)}
            error={Boolean(err)}
            helperText={err ?? q.help}
            disabled={disabled}
            sx={{ maxWidth: 260 }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        );

      case 'month_year': {
        const [mm = '', yyyy = ''] = typeof value === 'string' ? value.split('/') : [];
        const put = (m: string, y: string) => set(q.id, m || y ? `${m}/${y}` : '');
        return (
          <FormControl key={q.id} error={Boolean(err)} required={q.required} disabled={disabled}>
            <FormLabel sx={labelSx}>{q.label}</FormLabel>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="MM"
                value={mm}
                onChange={(e) => put(e.target.value.replace(/\D/g, '').slice(0, 2), yyyy)}
                error={Boolean(err)}
                disabled={disabled}
                sx={{ width: 90 }}
                slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': `${q.label} month` } }}
              />
              <Typography variant="h6" component="span">
                /
              </Typography>
              <TextField
                label="YYYY"
                value={yyyy}
                onChange={(e) => put(mm, e.target.value.replace(/\D/g, '').slice(0, 4))}
                error={Boolean(err)}
                disabled={disabled}
                sx={{ width: 120 }}
                slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': `${q.label} year` } }}
              />
            </Stack>
            {(err || q.help) && <FormHelperText>{err ?? q.help}</FormHelperText>}
          </FormControl>
        );
      }

      case 'yesno':
        return (
          <FormControl key={q.id} error={Boolean(err)} required={q.required} disabled={disabled}>
            <FormLabel sx={labelSx}>{q.label}</FormLabel>
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={typeof value === 'string' ? value : null}
              onChange={(_, v: string | null) => {
                if (v) set(q.id, v);
              }}
              aria-label={q.label}
              disabled={disabled}
            >
              {['Yes', 'No'].map((opt) => (
                <ToggleButton
                  key={opt}
                  value={opt}
                  sx={{ minWidth: 120, minHeight: 52, fontSize: '1.05rem', textTransform: 'none' }}
                >
                  {opt}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {(err || q.help) && <FormHelperText>{err ?? q.help}</FormHelperText>}
          </FormControl>
        );

      case 'single': {
        const options = q.options ?? [];
        const str = typeof value === 'string' ? value : '';
        const isOther = Boolean(q.allow_other) && (otherOn[q.id] || (str !== '' && !options.includes(str)));
        const selected = isOther ? OTHER : str;
        return (
          <FormControl key={q.id} error={Boolean(err)} required={q.required} disabled={disabled}>
            <FormLabel sx={labelSx}>{q.label}</FormLabel>
            <RadioGroup
              value={selected}
              onChange={(e) => {
                const v = e.target.value;
                if (v === OTHER) {
                  setOtherOn((o) => ({ ...o, [q.id]: true }));
                  set(q.id, '');
                } else {
                  setOtherOn((o) => ({ ...o, [q.id]: false }));
                  set(q.id, v);
                }
              }}
              row={options.length <= 4}
            >
              {options.map((opt) => (
                <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} sx={bigChoice} />
              ))}
              {q.allow_other && (
                <FormControlLabel value={OTHER} control={<Radio />} label="Other" sx={bigChoice} />
              )}
            </RadioGroup>
            {isOther && (
              <TextField
                label="Other — please specify"
                value={options.includes(str) ? '' : str}
                onChange={(e) => set(q.id, e.target.value)}
                disabled={disabled}
                autoFocus
                sx={{ mt: 1, maxWidth: 420 }}
              />
            )}
            {(err || q.help) && <FormHelperText>{err ?? q.help}</FormHelperText>}
          </FormControl>
        );
      }

      case 'multi': {
        const options = q.options ?? [];
        const list = Array.isArray(value) ? (value as string[]) : [];
        const chosen = list.filter((x) => options.includes(x));
        const extra = list.find((x) => !options.includes(x)) ?? '';
        const otherChecked = Boolean(q.allow_other) && (otherOn[q.id] || extra !== '');
        const write = (opts: string[], other: string) =>
          set(q.id, [...options.filter((o) => opts.includes(o)), ...(other ? [other] : [])]);
        return (
          <FormControl key={q.id} error={Boolean(err)} required={q.required} disabled={disabled}>
            <FormLabel sx={labelSx}>{q.label}</FormLabel>
            <FormGroup row={options.length <= 4}>
              {options.map((opt) => (
                <FormControlLabel
                  key={opt}
                  sx={bigChoice}
                  label={opt}
                  control={
                    <Checkbox
                      checked={chosen.includes(opt)}
                      onChange={(e) =>
                        write(
                          e.target.checked ? [...chosen, opt] : chosen.filter((x) => x !== opt),
                          extra,
                        )
                      }
                    />
                  }
                />
              ))}
              {q.allow_other && (
                <FormControlLabel
                  sx={bigChoice}
                  label="Other"
                  control={
                    <Checkbox
                      checked={otherChecked}
                      onChange={(e) => {
                        setOtherOn((o) => ({ ...o, [q.id]: e.target.checked }));
                        if (!e.target.checked) write(chosen, '');
                      }}
                    />
                  }
                />
              )}
            </FormGroup>
            {otherChecked && (
              <TextField
                label="Other — please specify"
                value={extra}
                onChange={(e) => write(chosen, e.target.value)}
                disabled={disabled}
                sx={{ mt: 1, maxWidth: 420 }}
              />
            )}
            {(err || q.help) && <FormHelperText>{err ?? q.help}</FormHelperText>}
          </FormControl>
        );
      }

      case 'percent_split': {
        const parts = q.parts ?? [];
        const split =
          value && typeof value === 'object' && !Array.isArray(value)
            ? (value as Record<string, unknown>)
            : {};
        const total = splitTotal(q, split);
        const ok = total !== null && Math.abs(total - 100) <= 1;
        return (
          <FormControl key={q.id} error={Boolean(err)} required={q.required} disabled={disabled}>
            <FormLabel sx={labelSx}>{q.label}</FormLabel>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {parts.map((part) => (
                <TextField
                  key={part}
                  label={part}
                  type="number"
                  value={split[part] === undefined ? '' : String(split[part])}
                  onChange={(e) => {
                    const next = { ...split };
                    if (e.target.value === '') delete next[part];
                    else next[part] = e.target.value;
                    set(q.id, next);
                  }}
                  disabled={disabled}
                  sx={{ width: { xs: '100%', sm: 160 } }}
                  slotProps={{
                    htmlInput: { inputMode: 'decimal', min: 0, max: 100 },
                    input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                  }}
                />
              ))}
            </Stack>
            {total !== null && (
              <Typography
                variant="body2"
                sx={{ mt: 1, color: ok ? 'success.main' : 'warning.main', fontWeight: 500 }}
              >
                {ok
                  ? `Adds up to ${fmt(total)}%`
                  : `Adds up to ${fmt(total)}% — should be 100%`}
              </Typography>
            )}
            {(err || q.help) && <FormHelperText>{err ?? q.help}</FormHelperText>}
          </FormControl>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Stack spacing={3}>
      {showHeader && (
        <Box>
          <Typography variant="h5" component="h2">
            {definition.title}
          </Typography>
          {definition.subtitle && (
            <Typography variant="subtitle1" color="text.secondary">
              {definition.subtitle}
            </Typography>
          )}
          {(definition.irb_approval || definition.irb_expiration) && (
            <Typography variant="caption" color="text.secondary" component="div">
              {definition.irb_approval && `IRB approval: ${definition.irb_approval}`}
              {definition.irb_approval && definition.irb_expiration && ' · '}
              {definition.irb_expiration && `Expires: ${definition.irb_expiration}`}
            </Typography>
          )}
          {definition.intro && (
            <Typography sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>{definition.intro}</Typography>
          )}
        </Box>
      )}
      {definition.questions.filter((q) => isQuestionVisible(q, answers)).map(renderQuestion)}
    </Stack>
  );
}
